import { z } from "zod";
import type { RfqTypeConfig, RfqFormModeConfig, FormMode } from "./config";
import type { SubmissionChannel } from "@convex/constants/channels";
import type { RfqType } from "@convex/constants/types";
import type { RfqContactInput, RfqLocationInput, RfqCreateArgs } from "./types";
import { createEmptyLocation } from "./types";
import type { RfqFormValues } from "./rfq-form-types";
import {
  PRODUCT_TYPE_CONDITIONS,
  isProductType,
} from "@/lib/generated/product-types";
import {
  getDimensionFieldsForType,
  getFieldsForType,
} from "@/lib/generated/product-type-fields";
import { isSpecFieldRequiredInFlow } from "@shared/validation/product/validators";

export function isHeightRequiredForType(productTypeSlug?: string): boolean {
  if (!productTypeSlug || !isProductType(productTypeSlug)) return false;
  return getDimensionFieldsForType(productTypeSlug).some(
    (field) => field.name === "height" && field.required,
  );
}

function hasLengthWidthInputs(productTypeSlug?: string): boolean {
  if (!productTypeSlug || !isProductType(productTypeSlug)) return false;
  const names = getDimensionFieldsForType(productTypeSlug).map((field) => field.name);
  return names.includes("length") && names.includes("width");
}

interface DimensionValues {
  length?: number;
  width?: number;
  height?: number;
}

function isPositive(value: number | undefined): boolean {
  return (value ?? 0) > 0;
}

export function missingDimensionFields(
  dimensions: DimensionValues | undefined,
  productTypeSlug: string | undefined,
  requireDimensions: boolean,
): string[] {
  if (!requireDimensions || !hasLengthWidthInputs(productTypeSlug)) return [];
  const missing: string[] = [];
  if (!isPositive(dimensions?.length)) missing.push("dimensionLength");
  if (!isPositive(dimensions?.width)) missing.push("dimensionWidth");
  if (isHeightRequiredForType(productTypeSlug) && !isPositive(dimensions?.height)) {
    missing.push("dimensionHeight");
  }
  return missing;
}

export function showsDimensionInputs(
  productTypeSlug: string | undefined,
  showAdvancedSpecs: boolean,
): boolean {
  return !showAdvancedSpecs || hasLengthWidthInputs(productTypeSlug);
}

export function isConditionRequiredForType(
  productTypeSlug: string | undefined,
  flow: RfqType,
): boolean {
  if (!productTypeSlug || !isProductType(productTypeSlug)) return false;
  if (!PRODUCT_TYPE_CONDITIONS[productTypeSlug]?.length) return false;
  return getFieldsForType(productTypeSlug).some(
    (field) =>
      field.name === "condition" && isSpecFieldRequiredInFlow(field, flow),
  );
}

export interface RfqValidationConfig {
  requireContact: boolean;
  requireDimensions: boolean;
  requireAskingPrice: boolean;
  requirePhotos: boolean;
}

export function createRfqValidationSchema(config: RfqValidationConfig) {
  const shape: Record<string, z.ZodTypeAny> = {
    rfqType: z.string().optional(),
    productTypeSlug: z.string().min(1, "Product type required"),
    productTypeName: z.string().optional(),
    location: z.any().optional(),
    additionalLocations: z.any().optional(),
    dimensions: z.any().optional(),
    attachments: z.any().optional(),
    askingPriceCents: z.number().optional(),
    budgetCents: z.number().optional(),
    condition: z.string().optional(),
    notes: z.string().optional(),
    productSpecs: z.any().optional(),
    hasHazmat: z.boolean().optional(),
    sourcePath: z.string().optional(),
    sourceReferrer: z.string().optional(),
  };
  if (config.requireContact) {
    shape.contact = z.object({
      firstName: z.string().min(1, "First name is required"),
      lastName: z.string().optional(),
      email: z.string().email("Valid email required"),
      phone: z.string().optional(),
      company: z.string().optional(),
    });
  } else {
    shape.contact = z.any().optional();
  }
  return z.object(shape).passthrough();
}

export function getSubmissionChannel(mode: FormMode): SubmissionChannel {
  return mode === "anonymous" ? "web_public" : "web_dashboard";
}

export function deriveValidationConfig(
  modeConfig: RfqFormModeConfig,
  typeConfig: RfqTypeConfig,
): RfqValidationConfig {
  return {
    requireContact: modeConfig.requireContact,
    requireDimensions: modeConfig.requireDimensions,
    requireAskingPrice: typeConfig.showAskingPrice,
    requirePhotos:
      typeConfig.requiresInventoryDetails && modeConfig.enforcePhotoRequirement,
  };
}

function defaultLocation(
  location: Partial<RfqLocationInput["address"]> | undefined,
  quantity: number | undefined,
): RfqLocationInput {
  return {
    ...createEmptyLocation(),
    address: {
      line1: location?.line1 ?? "",
      city: location?.city ?? "",
      state: location?.state ?? "",
      postalCode: location?.postalCode ?? "",
      country: location?.country ?? "US",
    },
    quantity,
  };
}

function mergeDefaultContact(
  initialContact: Partial<RfqContactInput> | undefined,
  baseContact: RfqFormValues["contact"],
) {
  return {
    firstName: initialContact?.firstName ?? baseContact?.firstName ?? "",
    lastName: initialContact?.lastName ?? baseContact?.lastName ?? "",
    email: initialContact?.email ?? baseContact?.email ?? "",
    phone: initialContact?.phone ?? baseContact?.phone ?? "",
    company: initialContact?.company ?? baseContact?.company,
  };
}

export function createDefaultValues(options: {
  rfqType: RfqType;
  productSlug?: string;
  productName?: string;
  contact?: Partial<RfqContactInput>;
  location?: Partial<RfqLocationInput["address"]>;
  quantity?: number;
  initialValues?: Partial<RfqCreateArgs>;
}): RfqFormValues {
  const baseValues: RfqFormValues = {
    rfqType: options.rfqType,
    productTypeSlug: options.productSlug ?? "",
    productTypeName: options.productName ?? "",
    location: defaultLocation(options.location, options.quantity),
    contact: {
      firstName: options.contact?.firstName ?? "",
      lastName: options.contact?.lastName ?? "",
      email: options.contact?.email ?? "",
      phone: options.contact?.phone ?? "",
      company: options.contact?.company,
    },
    sourcePath:
      typeof window !== "undefined" ? window.location.pathname : undefined,
    sourceReferrer:
      typeof window !== "undefined"
        ? document.referrer || undefined
        : undefined,
  };

  return {
    ...baseValues,
    ...options.initialValues,
    location: options.initialValues?.location ?? baseValues.location,
    additionalLocations: options.initialValues?.additionalLocations,
    contact: mergeDefaultContact(
      options.initialValues?.contact,
      baseValues.contact,
    ),
  };
}
